import type { OwnerType } from "@bt/domain";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DownloadIcon,
  FileIcon,
  FileTextIcon,
  ImageIcon,
  ImagePlusIcon,
  Loader2Icon,
  MoreHorizontalIcon,
  StarIcon,
  Trash2Icon,
  UploadIcon,
} from "lucide-react";
import { type ClipboardEvent, type DragEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { errorMessage } from "@/lib/errors";
import { useSystemInfo } from "@/lib/system";
import { type RouterOutputs, useTRPC } from "@/lib/trpc";
import { fileUrl, uploadAttachment } from "@/lib/upload";
import { cn } from "@/lib/utils";

type Attachment = RouterOutputs["parts"]["get"]["attachments"][number];

/** Uploading, unless the host takes no uploads (the public demo, ADR 0012). */
function useUpload(ownerType: OwnerType, ownerId: string) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(0);
  const enabled = useSystemInfo()?.capabilities.uploads ?? true;
  const upload = async (files: File[], kind: "image" | "document") => {
    if (!enabled || files.length === 0) return;
    setBusy((n) => n + files.length);
    try {
      for (const file of files) {
        const fileKind = kind === "image" && !file.type.startsWith("image/") ? "document" : kind;
        await uploadAttachment({ ownerType, ownerId, kind: fileKind, file });
      }
      toast.success(t("attachments.uploaded"));
    } catch (error) {
      toast.error(errorMessage(error, t));
    } finally {
      setBusy((n) => n - files.length);
      void queryClient.invalidateQueries();
    }
  };
  return { upload, busy: busy > 0, enabled };
}

function useAttachmentActions() {
  const trpc = useTRPC();
  return {
    remove: useMutation(trpc.attachments.delete.mutationOptions()),
    makeFirst: useMutation(trpc.attachments.makeFirst.mutationOptions()),
  };
}

/** Image strip with a large cover, drag & drop, paste and camera capture on phones. */
export function ImageGallery({
  ownerType,
  ownerId,
  attachments,
  compact = false,
}: {
  ownerType: OwnerType;
  ownerId: string;
  attachments: Attachment[];
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const images = attachments.filter((a) => a.kind === "image");
  const { upload, busy, enabled } = useUpload(ownerType, ownerId);
  const actions = useAttachmentActions();
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [viewing, setViewing] = useState<Attachment | null>(null);
  if (!enabled && images.length === 0) return null;

  const onDrop = (event: DragEvent) => {
    event.preventDefault();
    setDragging(false);
    void upload([...event.dataTransfer.files], "image");
  };
  const onPaste = (event: ClipboardEvent) => {
    const files = [...event.clipboardData.files].filter((f) => f.type.startsWith("image/"));
    if (files.length) {
      event.preventDefault();
      void upload(files, "image");
    }
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: drop and paste target around accessible buttons
    <div
      onDragOver={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      onPaste={onPaste}
      className={cn(
        "rounded-xl transition",
        dragging && "ring-2 ring-brand ring-offset-2 ring-offset-background",
      )}
    >
      {enabled ? (
        <input
          ref={input}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(event) => {
            void upload([...(event.target.files ?? [])], "image");
            event.target.value = "";
          }}
        />
      ) : null}
      {images.length === 0 ? (
        <button
          type="button"
          onClick={() => input.current?.click()}
          className={cn(
            "flex w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-muted/40 text-sm text-muted-foreground transition hover:border-input hover:bg-muted",
            compact ? "py-6" : "aspect-[4/3]",
          )}
        >
          {busy ? (
            <Loader2Icon className="size-5 animate-spin" />
          ) : (
            <ImagePlusIcon className="size-6" strokeWidth={1.5} />
          )}
          <span className="max-w-56 px-4 text-center text-xs">
            {busy ? t("attachments.uploading") : t("attachments.dropImages")}
          </span>
        </button>
      ) : (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => setViewing(images[0] ?? null)}
            className="group relative block aspect-[4/3] w-full overflow-hidden rounded-xl bg-muted"
          >
            <img
              src={fileUrl(images[0]!.fileId)}
              alt=""
              className="size-full object-cover transition duration-300 group-hover:scale-[1.02]"
            />
          </button>
          <div className="flex flex-wrap gap-2">
            {images.slice(1).map((image) => (
              <button
                key={image.id}
                type="button"
                onClick={() => setViewing(image)}
                className="size-14 overflow-hidden rounded-lg bg-muted ring-1 ring-border transition hover:ring-input"
              >
                <img
                  src={fileUrl(image.fileId, { thumb: true })}
                  alt=""
                  loading="lazy"
                  className="size-full object-cover"
                />
              </button>
            ))}
            {enabled ? (
              <button
                type="button"
                onClick={() => input.current?.click()}
                aria-label={t("attachments.addImage")}
                className="flex size-14 items-center justify-center rounded-lg border border-dashed border-border text-muted-foreground transition hover:border-input hover:bg-muted"
              >
                {busy ? (
                  <Loader2Icon className="size-4 animate-spin" />
                ) : (
                  <ImagePlusIcon className="size-4" />
                )}
              </button>
            ) : null}
          </div>
        </div>
      )}
      <Dialog open={viewing !== null} onOpenChange={(open) => !open && setViewing(null)}>
        <DialogContent wide className="p-0">
          {viewing ? (
            <div className="flex flex-col">
              <img
                src={fileUrl(viewing.fileId)}
                alt=""
                className="max-h-[70vh] w-full bg-black object-contain"
              />
              <div className="flex items-center justify-end gap-2 border-t border-border p-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    actions.makeFirst.mutate(
                      { id: viewing.id },
                      { onSuccess: () => setViewing(null) },
                    )
                  }
                >
                  <StarIcon />
                  {t("attachments.makeCover")}
                </Button>
                <Button variant="ghost" size="sm" asChild>
                  <a href={fileUrl(viewing.fileId, { download: true })}>
                    <DownloadIcon />
                    {t("attachments.download")}
                  </a>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive"
                  onClick={() =>
                    actions.remove.mutate({ id: viewing.id }, { onSuccess: () => setViewing(null) })
                  }
                >
                  <Trash2Icon />
                  {t("common.delete")}
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** Documents (manuals, invoices, PDFs) with preview and download. */
export function DocumentList({
  ownerType,
  ownerId,
  attachments,
}: {
  ownerType: OwnerType;
  ownerId: string;
  attachments: Attachment[];
}) {
  const { t } = useTranslation();
  const documents = attachments.filter((a) => a.kind === "document");
  const { upload, busy, enabled } = useUpload(ownerType, ownerId);
  const actions = useAttachmentActions();
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: drop target around accessible buttons
    <div
      onDragOver={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        void upload([...event.dataTransfer.files], "document");
      }}
      className={cn(
        "flex flex-col gap-1.5 rounded-xl",
        dragging && "ring-2 ring-brand ring-offset-2 ring-offset-background",
      )}
    >
      {enabled ? (
        <input
          ref={input}
          type="file"
          multiple
          hidden
          onChange={(event) => {
            void upload([...(event.target.files ?? [])], "document");
            event.target.value = "";
          }}
        />
      ) : null}
      {documents.map((doc) => {
        const Icon =
          doc.mime === "application/pdf"
            ? FileTextIcon
            : doc.mime.startsWith("image/")
              ? ImageIcon
              : FileIcon;
        return (
          <div
            key={doc.id}
            className="group flex items-center gap-3 rounded-lg border border-border bg-surface px-3 py-2"
          >
            <Icon className="size-4 shrink-0 text-muted-foreground" />
            <a
              href={fileUrl(doc.fileId)}
              target="_blank"
              rel="noreferrer"
              className="min-w-0 flex-1 truncate text-sm font-medium hover:underline"
            >
              {doc.title ?? doc.originalName}
            </a>
            <span className="hidden shrink-0 text-xs text-muted-foreground tabular sm:block">
              {formatSize(doc.size)}
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm" aria-label={t("common.more")}>
                  <MoreHorizontalIcon />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <a href={fileUrl(doc.fileId, { download: true })}>
                    <DownloadIcon />
                    {t("attachments.download")}
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem
                  destructive
                  onSelect={() => actions.remove.mutate({ id: doc.id })}
                >
                  <Trash2Icon />
                  {t("common.delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      })}
      {enabled ? (
        <button
          type="button"
          onClick={() => input.current?.click()}
          className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-border px-3 py-2.5 text-sm text-muted-foreground transition hover:border-input hover:bg-muted hover:text-foreground"
        >
          {busy ? (
            <Loader2Icon className="size-4 animate-spin" />
          ) : (
            <UploadIcon className="size-4" />
          )}
          {busy ? t("attachments.uploading") : t("attachments.dropHere")}
        </button>
      ) : null}
    </div>
  );
}
